import React, { useEffect } from 'react';
import axios from 'axios';

import { useAuth } from '../contexts/AuthContext';
import { getApiBaseUrl } from '../utils/backendBaseUrl';
import { useWorkspace, type WorkspaceStudent, type WorkspaceClass } from './state/workspaceStore';

const API = getApiBaseUrl();

function displayNameFromStudent(s: any): string {
  const alias =
    (typeof s?.publicAlias === 'string' && s.publicAlias.trim()) ||
    (typeof s?.alias === 'string' && s.alias.trim()) ||
    (typeof s?.public_alias === 'string' && s.public_alias.trim());
  if (alias) return alias;

  const anyLabel =
    (typeof s?.label === 'string' && s.label.trim()) ||
    (typeof s?.name === 'string' && s.name.trim());
  if (anyLabel) return anyLabel;

  return 'Alunno';
}

function classNameFromStudent(s: any): string {
  const classe = typeof s?.classe === 'string' ? s.classe.trim() : '';
  const sezione = typeof s?.sezione === 'string' ? s.sezione.trim() : '';
  const out = `${classe}${sezione}`.trim();
  return out || '—';
}

export default function WorkspaceBootstrapper() {
  const ws = useWorkspace();
  const { currentUser } = useAuth() as any;

  useEffect(() => {
    let mounted = true;

    async function loadStudents() {
      if (!currentUser) return;
      ws.setStudentsLoading(true);
      ws.setStudentsError(null);
      try {
        const token = await currentUser.getIdToken();
        const res = await axios.get(`${API}/alunni`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 8000
        });

        const raw = Array.isArray(res.data) ? res.data : res.data?.alunni || [];
        const students: WorkspaceStudent[] = raw.map((s: any, idx: number) => {
          const id = String(s?.id || s?._id || s?.uid || idx);
          const label = displayNameFromStudent(s);
          const className =
            classNameFromStudent(s) ||
            String((typeof s?.className === 'string' && s.className) || (typeof s?.classeNome === 'string' && s.classeNome) || '—');
          return { id, label, className, status: 'ok' };
        });

        if (!mounted) return;
        ws.setStudents(students);
        ws.setStudentsLoading(false);
        ws.setStudentsError(null);
      } catch (err) {
        if (!mounted) return;
        ws.setStudents([]);
        ws.setStudentsLoading(false);
        const message = axios.isAxiosError(err)
          ? `Impossibile caricare gli alunni (${err.response?.status || 'errore rete'}).`
          : 'Impossibile caricare gli alunni.';
        ws.setStudentsError(message);
      }
    }

    loadStudents();
    return () => {
      mounted = false;
    };
  }, [currentUser, ws.studentsReloadToken, ws.setStudents, ws.setStudentsLoading, ws.setStudentsError]);

  useEffect(() => {
    let mounted = true;

    async function loadClasses() {
      if (!currentUser) return;
      ws.setClassesLoading(true);
      ws.setClassesError(null);
      try {
        const token = await currentUser.getIdToken();
        const res = await axios.get(`${API}/classi`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 8000
        });

        const raw = Array.isArray(res.data) ? res.data : res.data?.classi || [];
        const classes: WorkspaceClass[] = raw
          .map((c: any) => String(c?.classe || c?.className || c?.name || '').trim())
          .filter(Boolean)
          .map((className: string) => ({ className }));

        if (!mounted) return;
        ws.setClasses(classes);
        ws.setClassesLoading(false);
        ws.setClassesError(null);
      } catch (err) {
        if (!mounted) return;
        ws.setClasses([]);
        ws.setClassesLoading(false);
        const message = axios.isAxiosError(err)
          ? `Impossibile caricare le classi (${err.response?.status || 'errore rete'}).`
          : 'Impossibile caricare le classi.';
        ws.setClassesError(message);
      }
    }

    loadClasses();
    return () => {
      mounted = false;
    };
  }, [currentUser, ws.classesReloadToken, ws.setClasses, ws.setClassesLoading, ws.setClassesError]);

  return null;
}
